import { useContext, useMemo, useState } from 'react';
import {
    Card,
    Grid,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    Typography
} from '@mui/material';
import { hoursOrMinutes, useFormatting } from '@/utils/formatting';
import { useTranslation } from 'react-i18next';
import { ShiftStatusWithdrawAllowed } from '@instapaid/shared-logic';
import { UserContext } from '@/contexts/user/UserContext';
import {
    TableCell,
    TableCellFirstCell,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellFirstCell
} from '@/components/shared/styles/Table';
import Chip, { chipColors } from '@/components/shared/Chip';
import { Shift } from '@/services/PaymentsServiceV2';
import CardHeader from '@/components/shared/CardHeader';
import { SMBold } from '@/typography/Typography';
import { Status } from './Status';

export enum ShiftListType {
    AVAILABLE = 'AVAILABLE',
    WORKED = 'WORKED',
    UPCOMING = 'UPCOMING'
}

export type ShiftsTableProps = {
    userId: string;
    payrollPeriod: string;
    shiftListType: ShiftListType;
    shifts?: Shift[];
    shiftStatusWithdrawAllowed?: ShiftStatusWithdrawAllowed;
    payoutType: string;
    companyName: string;
    heading?: string;
    totalHours?: number;
    totalEarnings?: number;
};

export const ShiftsTable = ({
    shifts,
    shiftStatusWithdrawAllowed,
    payoutType,
    heading,
    companyName,
    totalHours,
    totalEarnings
}: ShiftsTableProps) => {
    const { company: adminCompany } = useContext(UserContext);
    const { getBalanceLabel, toDecimal, formatDate, formatDateTimeToTime } =
        useFormatting();

    const totalWorkBreak = shifts?.reduce(
        (acc, shift) => acc + shift.break * 60,
        0
    );

    const { t } = useTranslation();

    return (
        <>
            {shifts && shifts?.length > 0 ? (
                <Card>
                    <CardHeader
                        label={heading}
                        description={`${companyName} - ${t('payout_type', {
                            payoutType
                        })}`}
                        chip={
                            <Chip
                                icon={<span></span>}
                                label={
                                    <SMBold
                                        color={chipColors.success.text}
                                    >{`${shifts.length}`}</SMBold>
                                }
                                color={'success'}
                            />
                        }
                    />
                    <Grid md={12}></Grid>

                    <TableContainer component={Paper}>
                        <Table aria-label="shifts table">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCellFirstCell>
                                        Date
                                    </TableHeaderCellFirstCell>
                                    <TableHeaderCell>Time</TableHeaderCell>
                                    <TableHeaderCell>Pause</TableHeaderCell>
                                    <TableHeaderCell>
                                        Worked Hours
                                    </TableHeaderCell>
                                    <TableHeaderCell>Status</TableHeaderCell>

                                    <TableHeaderCell align="right">
                                        Earnings
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {shifts?.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCellFirstCell>
                                            {row.startTime
                                                ? formatDate(row.startTime)
                                                : ''}
                                        </TableCellFirstCell>
                                        <TableCell>
                                            {row.startTime && row.endTime ? (
                                                <>
                                                    {formatDateTimeToTime(
                                                        row.startTime
                                                    )}{' '}
                                                    -{' '}
                                                    {formatDateTimeToTime(
                                                        row.endTime
                                                    )}
                                                </>
                                            ) : (
                                                <>Period not reported</>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {hoursOrMinutes(row.break)}{' '}
                                        </TableCell>
                                        <TableCell style={{ width: '140px' }}>
                                            {toDecimal(row.duration, 1)}
                                        </TableCell>
                                        <TableCell style={{ width: '138px' }}>
                                            <Status
                                                availableForPayout={
                                                    row.availableForPayout
                                                }
                                                isShiftTypeExcluded={
                                                    row.isShiftTypeExcluded
                                                }
                                                shiftStatusWithdrawAllowed={
                                                    shiftStatusWithdrawAllowed
                                                }
                                                status={row.status}
                                            />
                                        </TableCell>

                                        <TableCell
                                            align="right"
                                            style={{
                                                color: row.isShiftTypeExcluded
                                                    ? 'grey'
                                                    : undefined
                                            }}
                                            // color={!row.availableForPayout}
                                        >
                                            {getBalanceLabel(
                                                row.amount,
                                                adminCompany.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        You earned
                                    </TableHeaderCell>
                                    <TableHeaderCell></TableHeaderCell>
                                    <TableHeaderCell>
                                        {toDecimal(totalWorkBreak, 0)} mins
                                    </TableHeaderCell>
                                    <TableHeaderCell style={{ width: '140px' }}>
                                        {toDecimal(totalHours, 2)}
                                    </TableHeaderCell>
                                    <TableCell></TableCell>

                                    <TableHeaderCell align="right">
                                        {getBalanceLabel(
                                            totalEarnings,
                                            adminCompany.currency
                                        )}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                        </Table>
                    </TableContainer>
                </Card>
            ) : (
                <Typography textAlign={'center'}>{t('no_shifts')}</Typography>
            )}
        </>
    );
};
